/**
 * ManageSchedule Component
 * Dialog for creating and editing maintenance schedules
 * 
 * Features:
 * - Multi-step form with validation
 * - Recurrence options supported by backend (daily/weekly/monthly/quarterly/yearly)
 * - Asset selection (optional)
 * - Client notification settings
 * - Assigned engineer selection (required)
 * - Handles both create and edit modes
 */

import React, { useContext, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Grid,
  Chip
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { MaintenanceContext } from '../contexts/MaintenanceContext';
import { ClientContext } from '../contexts/ClientContext';
import { AdminContext } from '../contexts/AdminContext';
import moment from 'moment';

const ManageSchedule = ({ open, onClose, onSaved, editMode = false }) => {
  const {
    createSchedule,
    updateSchedule,
    selectedSchedule,
    assets,
    loadAssets,
    isLoading
  } = useContext(MaintenanceContext);
  
  const { clients, loadClients } = useContext(ClientContext);
  const { users, loadUsers } = useContext(AdminContext);
  const { loadClientEmails, clientEmails } = useContext(MaintenanceContext);

  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    asset_id: '',
    title: '',
    description: '',
    frequency: 'monthly',
    interval_value: 1,
    day_of_week: null,
    day_of_month: null,
    month_of_year: null,
    start_date: moment().format('YYYY-MM-DD'),
    end_date: '',
    status: 'active',
    expected_downtime_minutes: '',
    notify_client: false,
    assigned_to_user_id: ''
  });

  const [errors, setErrors] = useState({});

  // Load assets, clients, and users when dialog opens
  useEffect(() => {
    if (open) {
      loadAssets();
      loadClients();
      loadUsers(true); // Load only active users
    }
    // eslint-disable-next-line
  }, [open]);

  // Populate form when editing
  useEffect(() => {
    if (editMode && selectedSchedule?.schedule) {
      const schedule = selectedSchedule.schedule;
      setFormData({
        client_id: schedule.client_id || '',
        asset_id: schedule.asset_id || '',
        title: schedule.title || '',
        description: schedule.description || '',
        frequency: schedule.frequency || 'monthly',
        interval_value: schedule.interval_value || 1,
        day_of_week: schedule.day_of_week ?? null,
        day_of_month: schedule.day_of_month ?? null,
        month_of_year: schedule.month_of_year ?? null,
        start_date: schedule.start_date ? moment(schedule.start_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        end_date: schedule.end_date ? moment(schedule.end_date).format('YYYY-MM-DD') : '',
        status: schedule.status || 'active',
        expected_downtime_minutes: schedule.expected_downtime_minutes || '',
        notify_client: !!schedule.notify_client,
        assigned_to_user_id: schedule.assigned_to_user_id || ''
      });
    } else if (!editMode) {
      // Reset form for create mode
      setFormData({
        client_id: '',
        asset_id: '',
        title: '',
        description: '',
        frequency: 'monthly',
        interval_value: 1,
        day_of_week: null,
        day_of_month: 1,
        month_of_year: null,
        start_date: moment().format('YYYY-MM-DD'),
        end_date: '',
        status: 'active',
        expected_downtime_minutes: '',
        notify_client: false,
        assigned_to_user_id: ''
      });
    }
    setErrors({});
  }, [editMode, selectedSchedule, open]);

  // Filter assets by selected client
  const filteredAssets = formData.client_id
    ? assets.filter(a => a.client_id === parseInt(formData.client_id))
    : [];

  // Handle field change
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
    
    // Reset asset when client changes
    if (field === 'client_id') {
      setFormData(prev => ({
        ...prev,
        asset_id: ''
      }));
      // Load client emails for notification selection
      if (value) {
        loadClientEmails(value);
      } else {
        loadClientEmails(null);
      }
    }
    
    // Reset per-frequency options when frequency changes
    if (field === 'frequency') {
      setFormData(prev => ({
        ...prev,
        interval_value: 1,
        day_of_week: null,
        day_of_month: field === 'daily' ? null : (field === 'monthly' || field === 'quarterly' ? 1 : null),
        month_of_year: field === 'yearly' ? 1 : null
      }));
    }
  };


  // Validate form
  const validate = () => {
    const newErrors = {};
    
    if (!formData.client_id) {
      newErrors.client_id = 'Client is required';
    }
    
    if (!formData.title || formData.title.trim() === '') {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    
    // Required fields for recurrence
    if (formData.frequency === 'weekly' && (formData.day_of_week === null || formData.day_of_week === undefined)) {
      newErrors.day_of_week = 'Select a day of the week';
    }
    if (formData.frequency === 'monthly' && !formData.day_of_month) {
      newErrors.day_of_month = 'Specify day of month';
    }
    if (formData.frequency === 'quarterly' && !formData.day_of_month) {
      newErrors.day_of_month = 'Specify day of month';
    }
    if (formData.frequency === 'yearly' && (!formData.month_of_year || !formData.day_of_month)) {
      newErrors.month_of_year = 'Specify month';
      newErrors.day_of_month = 'Specify day of month';
    }
    if (!formData.assigned_to_user_id) {
      newErrors.assigned_to_user_id = 'Assigned engineer is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validate()) {
      return;
    }
    
    // Prepare payload
    // Build backend payload (normalize types and names)
    const payload = {
      client_id: parseInt(formData.client_id, 10),
      asset_id: formData.asset_id ? parseInt(formData.asset_id, 10) : null,
      title: formData.title,
      description: formData.description || null,
      frequency: formData.frequency,
      interval_value: formData.interval_value ? parseInt(formData.interval_value, 10) : 1,
      day_of_week: formData.day_of_week === null || formData.day_of_week === '' ? null : parseInt(formData.day_of_week, 10),
      day_of_month: formData.day_of_month === null || formData.day_of_month === '' ? null : parseInt(formData.day_of_month, 10),
      month_of_year: formData.month_of_year === null || formData.month_of_year === '' ? null : parseInt(formData.month_of_year, 10),
      start_date: formData.start_date,
      end_date: formData.end_date || null,
      max_occurrences: null,
      assigned_to_user_id: parseInt(formData.assigned_to_user_id, 10),
      notify_client: !!formData.notify_client,
      expected_downtime_minutes: formData.expected_downtime_minutes !== '' ? parseInt(formData.expected_downtime_minutes, 10) : null
    };
    
    let success;
    if (editMode && selectedSchedule?.schedule) {
      success = await updateSchedule(selectedSchedule.schedule.id, payload);
    } else {
      success = await createSchedule(payload);
    }
    
    if (success) {
      onSaved();
    }
  };

  // Render frequency configuration (backend-supported)
  const renderFrequencyConfig = () => {
    switch (formData.frequency) {
      case 'once':
        return (
          <Typography variant="body2" color="textSecondary">
            This schedule will run only once on the start date.
          </Typography>
        );
      
      case 'daily':
        return (
          <Typography variant="body2" color="textSecondary">
            This schedule will run every day.
          </Typography>
        );
      
      case 'weekly':
        return (
          <Box>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={formData.day_of_week !== null ? formData.day_of_week : ''}
                onChange={(e) => handleChange('day_of_week', e.target.value === '' ? null : parseInt(e.target.value))}
                label="Day of Week"
              >
                <MenuItem value=""><em>Select</em></MenuItem>
                <MenuItem value={0}>Sunday</MenuItem>
                <MenuItem value={1}>Monday</MenuItem>
                <MenuItem value={2}>Tuesday</MenuItem>
                <MenuItem value={3}>Wednesday</MenuItem>
                <MenuItem value={4}>Thursday</MenuItem>
                <MenuItem value={5}>Friday</MenuItem>
                <MenuItem value={6}>Saturday</MenuItem>
              </Select>
            </FormControl>
            {errors.day_of_week && (
              <Typography variant="caption" color="error">{errors.day_of_week}</Typography>
            )}
          </Box>
        );
      
      case 'monthly':
        return (
          <Box>
            <TextField
              label="Day of Month (1-31)"
              type="number"
              fullWidth
              margin="normal"
              variant="outlined"
              value={formData.day_of_month || ''}
              onChange={(e) => handleChange('day_of_month', e.target.value ? Math.max(1, Math.min(31, parseInt(e.target.value))) : '')}
              inputProps={{ min: 1, max: 31 }}
              error={Boolean(errors.day_of_month)}
              helperText={errors.day_of_month || ''}
            />
          </Box>
        );
      
      case 'quarterly':
        return (
          <Box>
            <TextField
              label="Day of Month (1-31)"
              type="number"
              fullWidth
              margin="normal"
              variant="outlined"
              value={formData.day_of_month || ''}
              onChange={(e) => handleChange('day_of_month', e.target.value ? Math.max(1, Math.min(31, parseInt(e.target.value))) : '')}
              inputProps={{ min: 1, max: 31 }}
            />
            {errors.day_of_month && (
              <Typography variant="caption" color="error">{errors.day_of_month}</Typography>
            )}
          </Box>
        );
      
      case 'yearly':
        return (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={formData.month_of_year || 1}
                    onChange={(e) => handleChange('month_of_year', parseInt(e.target.value))}
                    label="Month"
                  >
                    {moment.months().map((month, index) => (
                      <MenuItem key={index} value={index + 1}>{month}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Day (1-31)"
                  type="number"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  value={formData.day_of_month || 1}
                  onChange={(e) => handleChange('day_of_month', e.target.value ? Math.max(1, Math.min(31, parseInt(e.target.value))) : 1)}
                  inputProps={{ min: 1, max: 31 }}
                />
              </Grid>
            </Grid>
            {(errors.month_of_year || errors.day_of_month) && (
              <Typography variant="caption" color="error">
                {errors.month_of_year || errors.day_of_month}
              </Typography>
            )}
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editMode ? 'Edit Schedule' : 'Create New Schedule'}
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Client Selection */}
          <Grid item xs={12} sm={6}>
            {(() => {
              const clientOptions = (clients || []).map((c) => ({
                id: c.id !== undefined ? c.id : c.ID,
                name: c.name !== undefined ? c.name : c.Name,
              }));

              const currentValue = clientOptions.find(
                (o) => String(o.id) === String(formData.client_id)
              ) || null;

              return (
                <Autocomplete
                  id="maintenance-client-autocomplete"
                  options={clientOptions}
                  getOptionLabel={(opt) => `${opt.id} | ${opt.name}`}
                  getOptionSelected={(option, value) => String(option.id) === String(value.id)}
                  value={currentValue}
                  onChange={(e, value) => {
                    handleChange('client_id', value ? value.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client"
                      variant="outlined"
                      required
                      error={Boolean(errors.client_id)}
                      helperText={errors.client_id || ''}
                    />
                  )}
                  fullWidth
                />
              );
            })()}
          </Grid>
          
          {/* Asset Selection (Optional) */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" disabled={!formData.client_id}>
              <InputLabel>Asset (Optional)</InputLabel>
              <Select
                value={formData.asset_id}
                onChange={(e) => handleChange('asset_id', e.target.value)}
                label="Asset (Optional)"
              >
                <MenuItem value="">
                  <em>No specific asset</em>
                </MenuItem>
                {filteredAssets.map(asset => (
                  <MenuItem key={asset.id} value={asset.id}>
                    {asset.asset_name} ({asset.asset_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              label="Title"
              fullWidth
              variant="outlined"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              error={Boolean(errors.title)}
              helperText={errors.title}
              required
              placeholder="e.g., Monthly WordPress Security Updates"
            />
          </Grid>
          
          {/* Description */}
          <Grid item xs={12}>
            <TextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Detailed description of the maintenance task..."
            />
          </Grid>
          
          {/* Frequency */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Frequency</InputLabel>
              <Select
                value={formData.frequency}
                onChange={(e) => handleChange('frequency', e.target.value)}
                label="Frequency"
              >
                <MenuItem value="once">One-time</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* Assigned Engineer */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Assigned Engineer</InputLabel>
              <Select
                value={formData.assigned_to_user_id}
                onChange={(e) => handleChange('assigned_to_user_id', e.target.value)}
                label="Assigned Engineer"
                error={Boolean(errors.assigned_to_user_id)}
              >
                <MenuItem value=""><em>Select</em></MenuItem>
                {(users || []).map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.name || `${u.first_name} ${u.last_name}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.assigned_to_user_id && (
              <Typography variant="caption" color="error">{errors.assigned_to_user_id}</Typography>
            )}
          </Grid>
          
          {/* Estimated Downtime */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Estimated Downtime (minutes)"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.expected_downtime_minutes}
              onChange={(e) => handleChange('expected_downtime_minutes', e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
          
          {/* Frequency Configuration */}
          <Grid item xs={12}>
            {renderFrequencyConfig()}
          </Grid>
          
          {/* Start Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              variant="outlined"
              value={formData.start_date}
              onChange={(e) => handleChange('start_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.start_date)}
              helperText={errors.start_date}
              required
            />
          </Grid>
          
          {/* End Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="End Date (Optional)"
              type="date"
              fullWidth
              variant="outlined"
              value={formData.end_date}
              onChange={(e) => handleChange('end_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank for ongoing schedule"
            />
          </Grid>
          
          {/* Notification Toggle */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.notify_client}
                  onChange={(e) => handleChange('notify_client', e.target.checked)}
                  color="primary"
                />
              }
              label="Notify Client About Maintenance Window"
            />
          </Grid>
          {/* Notification Email Selection */}
          {formData.notify_client && (
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Recipient Email</InputLabel>
                <Select
                  value={formData.notification_email || ''}
                  onChange={(e) => handleChange('notification_email', e.target.value)}
                  label="Recipient Email"
                >
                  <MenuItem value=""><em>Select</em></MenuItem>
                  {(clientEmails || []).map((e, idx) => (
                    <MenuItem key={`${e.email}-${idx}`} value={e.email}>
                      {e.email} {e.name ? `(${e.name})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={isLoading}
        >
          {editMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageSchedule;
